import { Link, useLocation } from "wouter";
import { useState, useEffect, useRef } from "react";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { products } from "@/lib/products";

// Product pages with a light hero need dark header text.
const LIGHT_PRODUCT_PATHS = new Set(products.filter((p) => p.theme === "light").map((p) => `/products/${p.slug}`));

export function Header() {
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const lastScrollY = useRef(0);

  const topUsesDarkText =
    location === "/blog" || location.startsWith("/blog/") || LIGHT_PRODUCT_PATHS.has(location);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const isAtTop = currentY < 60;
      const isScrollingUp = currentY < lastScrollY.current;

      setScrolled(!isAtTop);

      if (isAtTop) {
        setVisible(true);
      } else if (isScrollingUp) {
        setVisible(true);
      } else {
        setVisible(false);
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "WHY ORVEXACO", href: "/about" },
    { name: "PRODUCTS", href: "/products" },
    { name: "SERVICES", href: "/services" },
    { name: "INSIGHTS", href: "/blog" },
    { name: "CONTACT", href: "/contact" },
  ];

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-50"
      animate={{ y: visible ? 0 : "-110%" }}
      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* At top: full-width transparent layout */}
      <AnimatePresence mode="wait" initial={false}>
        {!scrolled ? (
          <motion.div
            key="top"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex justify-between items-center gap-4 p-6 md:p-2"
          >
            <Link href="/" className="flex shrink-0 items-center self-center leading-none">
              <img
                src={topUsesDarkText ? "/branding/logo_bg_black.svg" : "/branding/logo_bg_white.svg"}
                alt="Orvexaco"
                className="block h-11 w-auto md:h-18"
                width={200}
                height={48}
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex md:flex-1 md:items-center md:justify-end gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.href}
                  className={`relative group/nav px-3 py-1.5 text-[12px] md:text-[13px] font-mono transition-colors uppercase tracking-wider flex items-center gap-1.5 ${
                    topUsesDarkText ? "text-[#0A0A0A]" : "text-white"
                  }`}
                >
                  {/* Corner brackets */}
                  <span
                    className={`pointer-events-none absolute top-0 left-0 w-2 h-2 border-t border-l border-transparent transition-colors duration-150 ${
                      topUsesDarkText ? "group-hover/nav:border-[#0A0A0A]/60" : "group-hover/nav:border-white/60"
                    }`}
                  />
                  <span
                    className={`pointer-events-none absolute top-0 right-0 w-2 h-2 border-t border-r border-transparent transition-colors duration-150 ${
                      topUsesDarkText ? "group-hover/nav:border-[#0A0A0A]/60" : "group-hover/nav:border-white/60"
                    }`}
                  />
                  <span
                    className={`pointer-events-none absolute bottom-0 left-0 w-2 h-2 border-b border-l border-transparent transition-colors duration-150 ${
                      topUsesDarkText ? "group-hover/nav:border-[#0A0A0A]/60" : "group-hover/nav:border-white/60"
                    }`}
                  />
                  <span
                    className={`pointer-events-none absolute bottom-0 right-0 w-2 h-2 border-b border-r border-transparent transition-colors duration-150 ${
                      topUsesDarkText ? "group-hover/nav:border-[#0A0A0A]/60" : "group-hover/nav:border-white/60"
                    }`}
                  />
                  {/* Dot indicator */}
                  <span
                    className={`w-1.5 h-1.5 rounded-full bg-transparent transition-colors duration-150 shrink-0 ${
                      topUsesDarkText ? "group-hover/nav:bg-[#0A0A0A]" : "group-hover/nav:bg-white"
                    }`}
                  />
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Mobile Toggle */}
            <button
              className={`md:hidden p-1 transition-colors z-[60] ${
                topUsesDarkText ? "text-[#0A0A0A] hover:text-[#E86A3A]" : "text-white hover:text-[#7BBFB0]"
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </motion.div>
        ) : (
          /* Scrolled: compact centered pill */
          <motion.div
            key="scrolled"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="flex justify-center pt-4 px-4"
          >
            <div className="flex items-center bg-[#111318] border border-white/10 rounded-[20px] px-6 py-3 shadow-xl backdrop-blur-md">
              <Link href="/" className="mr-8 md:mr-10 shrink-0 block leading-none opacity-90 hover:opacity-100 transition-opacity">
                <img
                  src="/branding/logo_bg_white.svg"
                  alt="Orvexaco"
                  className="h-8 md:h-10 w-auto"
                  width={160}
                  height={40}
                />
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden md:flex items-center gap-5">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    href={link.href}
                    className="relative group/nav px-3 py-1.5 text-[12px] font-mono text-white transition-colors uppercase tracking-widest whitespace-nowrap flex items-center gap-1.5"
                  >
                    {/* Corner brackets */}
                    <span className="pointer-events-none absolute top-0 left-0 w-2 h-2 border-t border-l border-transparent group-hover/nav:border-white/60 transition-colors duration-150" />
                    <span className="pointer-events-none absolute top-0 right-0 w-2 h-2 border-t border-r border-transparent group-hover/nav:border-white/60 transition-colors duration-150" />
                    <span className="pointer-events-none absolute bottom-0 left-0 w-2 h-2 border-b border-l border-transparent group-hover/nav:border-white/60 transition-colors duration-150" />
                    <span className="pointer-events-none absolute bottom-0 right-0 w-2 h-2 border-b border-r border-transparent group-hover/nav:border-white/60 transition-colors duration-150" />
                    {/* Dot indicator */}
                    <span className="w-1.5 h-1.5 rounded-full bg-transparent group-hover/nav:bg-white transition-colors duration-150 shrink-0" />
                    {link.name}
                  </Link>
                ))}
              </nav>

              {/* Mobile Toggle */}
              <button
                className="md:hidden text-white p-1 hover:text-[#7BBFB0] transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0A0A0A] z-50 flex flex-col justify-center items-center gap-8"
          >
            {navLinks.map((link, idx) => (
              <motion.div
                key={link.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link
                  href={link.href}
                  className="text-2xl font-mono text-white hover:text-[#7BBFB0] transition-colors uppercase tracking-widest"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
